import {
    ATTRIBUTES,
    openWaitlistModal,
    PUB_SUB_EVENTS,
    SELECTORS,
    createATCitems,
    publish,
    openPersonalizationPopup
} from '@/scripts/core/global'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { SizeGuide } from '@/scripts/theme/size-guide'
import { SizePopupDialogue } from '@/scripts/product/size-popup-dialogue'
import {
    addItemsToCart,
    getDOMCartSectionApiIds,
    renderResponseToCartDrawer
} from '@/scripts/core/cart-functions'

export class ProductStickyAtc extends UcoastEl {
    static htmlSelector = 'product-sticky-atc'
    static selectors = {
        form: 'form',
        submitButton: '[type="submit"]',
        formIdEl: '[name="id"]',
        variantLabel: '.product-sticky-atc__variant-label',
        variantItems: '.product-sticky-atc__variant-button',
        variantDropdown: '.product-sticky-atc__variant-dropdown',
        genderInputs: '.product-sticky-atc-gender-input',
		triggerPersonalizationPopup: '.trigger-personalization-popup',
        toggleSizeGuide: '.open-size-guide'
    }

    form: HTMLFormElement
    formIdEl: HTMLInputElement
    submitButton: HTMLButtonElement
    variantLabel: HTMLButtonElement
    variantItems: NodeListOf<HTMLButtonElement>
    genderInputs: NodeListOf<HTMLInputElement>
    variantDropdown: HTMLElement
    triggerPersonalizationButton?: HTMLButtonElement
    hideErrors: boolean
    error?: boolean
    lastSection!: HTMLElement
    toggleSizeGuide: HTMLButtonElement
    private onBodyClickBound: (e: Event) => void

    constructor() {
        super()

        // Bind once so we can add/remove reliably during lifecycle
        this.onBodyClickBound = this.onClickBody.bind(this)

        this.form = q.rs(ProductStickyAtc.selectors.form, this)
        this.formIdEl = (this.form.querySelector('[name="id"]') as HTMLInputElement) || (this.form.querySelector('[name="items[0][id]"]') as HTMLInputElement)
        this.submitButton = q.rs(ProductStickyAtc.selectors.submitButton, this)
        this.variantLabel = q.rs(ProductStickyAtc.selectors.variantLabel, this)
        this.variantItems = q.rl(ProductStickyAtc.selectors.variantItems, this)
        this.variantDropdown = q.rs(ProductStickyAtc.selectors.variantDropdown, this)
        this.genderInputs = q.rl(ProductStickyAtc.selectors.genderInputs, this)
        this.triggerPersonalizationButton = this.querySelector(ProductStickyAtc.selectors.triggerPersonalizationPopup) as HTMLButtonElement
        this.toggleSizeGuide = q.rs(ProductStickyAtc.selectors.toggleSizeGuide, this)
        const hasMultipleVariants = !this.variantDropdown.classList.contains('hidden')
        if (hasMultipleVariants) {
            // Keep variant id input disabled until a size is explicitly selected
            this.formIdEl.disabled = true
            // Prevent ATC until valid size selection
            this.submitButton.setAttribute('aria-disabled', 'true')
            // Prevent personalization until valid size selection
            if (this.triggerPersonalizationButton) {
                this.triggerPersonalizationButton.setAttribute('disabled', 'true')
                this.triggerPersonalizationButton.setAttribute('aria-disabled', 'true')
            }
        } else {
            this.formIdEl.disabled = false
        }

        this.submitButton.addEventListener('click', this.onSubmitHandler.bind(this))
        this.variantLabel.addEventListener('click', this.toggleVariantDropdown.bind(this))
        this.variantItems.forEach(item => {
            item.addEventListener('click', this.onClickVariantItem.bind(this))
        })

        this.genderInputs.forEach(input => {
            input.addEventListener('change', this.onChangeGender.bind(this))
        })

        this.toggleSizeGuide.addEventListener('click', (e: Event) => {
            e.preventDefault()
            const sizeGuide = document.querySelector('size-guide') as SizeGuide
            if(sizeGuide) sizeGuide.open()
        })

        if(this.triggerPersonalizationButton) this.triggerPersonalizationButton.addEventListener('click', this.triggerPersonalization.bind(this))
        this.hideErrors = this.dataset.hideErrors === 'true'
        
        // Restore saved gender and size selections after component initialization
        this.restoreSavedSelections()
    }

    override connectedCallback(): void {
        // Manage global listener lifecycle to avoid duplicates
        document.body.addEventListener('click', this.onBodyClickBound)
        const target = q.rs('.product', document)
        const footer = q.rs('.shopify-section-group-footer-group', document)

        const handleScroll = () => {
            this.classList.toggle('active', ((window.innerWidth >= 750 && target.getBoundingClientRect().bottom <= 500 || window.innerWidth < 750)) && footer.getBoundingClientRect().top > footer.offsetHeight)
        }

        window.addEventListener('scroll', handleScroll, { passive: true })
        handleScroll()
    }

    override disconnectedCallback(): void {
        document.body.removeEventListener('click', this.onBodyClickBound)
    }

    triggerPersonalization(e: Event) {
        e.preventDefault()
        // Guard: do nothing if disabled or no explicit size selected
        const target = e.currentTarget as HTMLElement
		if(!target) return
        if (this.formIdEl.disabled || this.triggerPersonalizationButton?.getAttribute('aria-disabled') === 'true' || target.classList.contains('disabled')) return
        const id = String(this.formIdEl.value)
        const productUrl = String(this.dataset.url)
        openPersonalizationPopup(productUrl, id)
    }

    onChangeGender(e: Event) {
        e.preventDefault()
        const target = e.currentTarget as HTMLInputElement
        if(!target) return
        const selectedGender = target.value
        this.variantItems.forEach(item => {
            item.querySelectorAll('[data-gender]').forEach(_item => {
                const el = _item as HTMLElement
                el.classList.toggle('hidden', el.dataset.gender != selectedGender)
            })
        })
        
        // Update the label text if a size is already selected
        const activeVariantButton = q.os<HTMLButtonElement>('.product-sticky-atc__variant-button.active', this)
        if (activeVariantButton) {
            const labelText = q.rs('[data-uc-text]', this.variantLabel) as HTMLElement
            if (labelText) {
                const genderSpecificSpan = q.os<HTMLElement>(`[data-gender="${selectedGender}"]:not(.hidden)`, activeVariantButton)
                if (genderSpecificSpan && genderSpecificSpan.textContent) {
                    labelText.textContent = genderSpecificSpan.textContent.trim()
                } else {
                    // Fallback to base value if no gender-specific text found
                    const baseValue = activeVariantButton.dataset.value
                    if (baseValue) labelText.textContent = baseValue
                }
            }
        }
        
        // Save gender selection (preserve existing variant selection if any)
        const currentVariantId = activeVariantButton?.dataset.id || null
        const currentSizeDisplayName = activeVariantButton?.dataset.value || null
        this.saveSelections(selectedGender, currentVariantId, currentSizeDisplayName)
    }

    onClickBody(e: Event) {
        const target = e.target as HTMLElement
        if(!target) return

        if(!target.closest('.product-sticky-atc__variant-dropdown')) this.variantDropdown.classList.remove('active')
    }

    toggleVariantDropdown(e: Event) {
        e.preventDefault()
        if(!this.variantDropdown) return
        this.variantDropdown.classList.toggle('active')
    }

    onClickVariantItem(e: Event) {
        e.preventDefault()
        const target = e.currentTarget as HTMLButtonElement
        if(!target) return
        this.toggleVariantDropdown(e)

        const available = target.dataset.available == 'true'
        const inventoryStatus = target.dataset.inventoryStatus || ''
        const inventoryStatusText = target.dataset.inventoryStatusText || ''
        const preorder = inventoryStatus == 'PreOrder'
        const id = target.dataset.id
        const value = target.dataset.value
        const price = target.dataset.price
        if(!id || !value || !price) return

        // Save size selection
        const selectedGenderInput = q.os<HTMLInputElement>('.product-sticky-atc-gender-input:checked', this)
        const savedGender = selectedGenderInput?.value || null
        this.saveSelections(savedGender, id, value)

        this.formIdEl.value = id
        const labelText = q.rs('[data-uc-text]', this.variantLabel) as HTMLElement
        if(labelText) {
            // Get the selected gender to show the correct gender-specific size label
            const selectedGenderInput = q.os<HTMLInputElement>('.product-sticky-atc-gender-input:checked', this)
            const selectedGender = selectedGenderInput?.value || null
            
            // Try to find the visible gender-specific size text
            let displayText = value // fallback to base value
            if (selectedGender) {
                const genderSpecificSpan = q.os<HTMLElement>(`[data-gender="${selectedGender}"]:not(.hidden)`, target)
                if (genderSpecificSpan && genderSpecificSpan.textContent) {
                    displayText = genderSpecificSpan.textContent.trim()
                }
            }
            
            labelText.textContent = displayText
        }

        this.variantItems.forEach(item => {
            item.classList.remove('active')
        })
        target.classList.add('active')

        if(available)  this.submitButton.removeAttribute('data-oos-popup-trigger')
        else this.submitButton.setAttribute('data-oos-popup-trigger', `${this.dataset.url}?variant=${id}&section_id=waitlist`)

        // Enable variant id and actions now that a size has been explicitly selected
        this.formIdEl.disabled = false
        this.submitButton.removeAttribute('aria-disabled')
        if (this.triggerPersonalizationButton) {
            this.triggerPersonalizationButton.removeAttribute('disabled')
            this.triggerPersonalizationButton.removeAttribute('aria-disabled')
        }

        const instockEl = q.rs('[data-uc-inventory-status-instock]', this.submitButton)
        const outstockEl = q.rs('[data-uc-inventory-status-outstock]', this.submitButton)
        const preorderEl = q.rs('[data-uc-inventory-status-preorder]', this.submitButton)
        if(!instockEl || !outstockEl || !preorderEl) return

        if(preorder) {
			preorderEl.textContent = inventoryStatusText
		}

        instockEl.classList.toggle('hidden', (!available || preorder))
        outstockEl.classList.toggle('hidden', available)
		preorderEl.classList.toggle('hidden', (!available || !preorder))
		this.submitButton.dataset.preorder = String(preorder)

        this.variantLabel.querySelector('.product-sticky-atc__variant-label__first')?.classList.add('hidden')
        this.variantLabel.querySelector('.product-sticky-atc__variant-label__second')?.classList.remove('hidden')

        if (target.dataset.sizePopup === 'true') {
            const sectionEl = document.querySelector('section[id^="MainProduct-"]')
            const sectionIdAttr = sectionEl?.getAttribute('id') || ''
            const sectionId = sectionIdAttr.replace('shopify-section-', '').replace('MainProduct-', '')
            if (sectionId) {
                const sizePopupDialogue = q.os<SizePopupDialogue>(`#SizePopupDialogue-${sectionId}`, document)
                if (sizePopupDialogue && target) {
                    sizePopupDialogue.show(target)
                }
            }
        } else {
            const sectionEl = document.querySelector('section[id^="MainProduct-"]')
            const sectionIdAttr = sectionEl?.getAttribute('id') || ''
            const sectionId = sectionIdAttr.replace('shopify-section-', '').replace('MainProduct-', '')
            if (sectionId) {
                const sizePopupDialogue = q.os<SizePopupDialogue>(`#SizePopupDialogue-${sectionId}`, document)
                if (sizePopupDialogue) {
                    sizePopupDialogue.close()
                }
            }
        }

        if(window.innerWidth < 750) {
            const personalizationBadge = document.querySelector('product-info .price__badge-personalization') as HTMLElement
            if(personalizationBadge) {
                personalizationBadge.dataset.variantId = this.formIdEl.value
            }

            const personalizationTriggers = document.querySelectorAll<HTMLElement>('section[id^="MainProduct-"] .trigger-personalization-popup')
            personalizationTriggers.forEach(trigger => {
                trigger.classList.toggle('disabled', !available)
            })
        }
        if(!available) {
            openWaitlistModal(`${this.dataset.url}?variant=${id}&section_id=waitlist`, this.submitButton)
        }
    }

    getSpinner(target: HTMLButtonElement) {
        return q.rs(SELECTORS.loadingOverlaySpinner, target)
    }

    onSubmitHandler(event: Event) {
        event.preventDefault()
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return
        const oosUrl = q.oa(this.submitButton, 'data-oos-popup-trigger')
        if (oosUrl) {
            openWaitlistModal(oosUrl, this.submitButton)
            return
        }

        const preorder = this.submitButton.dataset.preorder == 'true'
        this.submitButton.setAttribute('aria-disabled', 'true')
        this.submitButton.setAttribute(ATTRIBUTES.loading, '')
        this.getSpinner(this.submitButton).classList.remove('hidden')

        const formData = new FormData(this.form)
        let formVariantId = formData.get('id') || formData.get('items[0][id]')
        if (!formVariantId) throw Error('No variant id found')
        const addedVariantId = parseInt(formVariantId.toString())
        
        type AddToCartItem = {
            id: number
            quantity: number
            selling_plan?: number
            properties: Record<string, string>
            parent_id?: number
        }
        let items: AddToCartItem[] = []
        if(formData.get('items[0][id]')) {
            if(preorder) formData.set('items[0][properties[_preorder]]', 'true')
            items = createATCitems(formData) as AddToCartItem[]
        } else {
            if(preorder) formData.set('_preorder', 'true')
        }

        addItemsToCart(items.length? items : formData, getDOMCartSectionApiIds())
            .then((cart) => {
                if ('status' in cart) {
                    return
                }

                publish(PUB_SUB_EVENTS.cartUpdate, {
                    source: 'product-form',
                    productVariantId: addedVariantId,
                })
                renderResponseToCartDrawer(cart)
            })
            .catch((e) => {
                console.error(e)
            })
            .finally(() => {
                this.submitButton.removeAttribute(ATTRIBUTES.loading)

                if (!this.error)
                    this.submitButton.removeAttribute('aria-disabled')
                this.getSpinner(this.submitButton).classList.add('hidden')
            })
    }

    private saveSelections(gender: string | null, variantId: string | null, sizeDisplayName: string | null) {
        const state = {
            gender,
            variantId,
            sizeDisplayName,
            productUrl: this.dataset.url || ''
        }
        sessionStorage.setItem('productStickyAtcState', JSON.stringify(state))
    }

    restoreSelections(savedGender: string | null, savedVariantId: string | null, savedSizeDisplayName: string | null) {
        // Always restore gender selection if it was saved
        if (savedGender) {
            const genderInput = q.os<HTMLInputElement>(`.product-sticky-atc-gender-input[value="${savedGender}"]`, this)
            if (genderInput) {
                genderInput.checked = true
                // Update size text visibility without triggering save
                this.variantItems.forEach(item => {
                    item.querySelectorAll('[data-gender]').forEach(_item => {
                        const el = _item as HTMLElement
                        el.classList.toggle('hidden', el.dataset.gender != savedGender)
                    })
                })
            }
        }

        // Only restore size selection if a size was actually selected before
        // This ensures ATC stays disabled if no size was selected
        if (savedVariantId || savedSizeDisplayName) {
            // Use requestAnimationFrame to ensure DOM updates from gender change are complete
            requestAnimationFrame(() => {
                // First try to find by variant ID
                let variantButton: HTMLButtonElement | null = null
                
                if (savedVariantId) {
                    variantButton = q.os<HTMLButtonElement>(`.product-sticky-atc__variant-button[data-id="${savedVariantId}"]`, this) || null
                }
                
                // If variant ID not found, try to match by display name
                if (!variantButton && savedSizeDisplayName) {
                    const allVariantButtons = q.ol<HTMLButtonElement>('.product-sticky-atc__variant-button', this)
                    if (allVariantButtons) {
                        for (const button of allVariantButtons) {
                            const value = button.dataset.value
                            if (!value) continue
                            
                            // Check if the size text matches (considering gender-specific text)
                            const sizeTextEls = q.ol<HTMLElement>('[data-gender]', button)
                            let matches = false
                            
                            if (!sizeTextEls || sizeTextEls.length === 0) {
                                // No gender-specific text, check the button's value directly
                                if (value === savedSizeDisplayName) {
                                    matches = true
                                }
                            } else {
                                // Check gender-specific text
                                for (const textEl of sizeTextEls) {
                                    const textContent = textEl.textContent?.trim()
                                    const isVisible = !textEl.classList.contains('hidden')
                                    const isMatchingGender = savedGender ? textEl.dataset.gender === savedGender : true
                                    
                                    if (textContent === savedSizeDisplayName && isVisible && isMatchingGender) {
                                        matches = true
                                        break
                                    }
                                }
                            }
                            
                            if (matches) {
                                variantButton = button
                                break
                            }
                        }
                    }
                }
                
                // Select the variant if found - this will enable the ATC button
                if (variantButton) {
                    // Trigger click to select the variant and update all UI
                    variantButton.click()
                }
                // If variant not found, ATC remains disabled (as initialized)
            })
        }
        // If no size was saved, ATC stays disabled (as initialized in constructor)
    }

    private restoreSavedSelections() {
        try {
            const savedStateStr = sessionStorage.getItem('productStickyAtcState')
            if (!savedStateStr) return
            
            const savedState = JSON.parse(savedStateStr)
            // Only restore if it's for the same product URL
            if (savedState.productUrl !== this.dataset.url) return

            const savedGender = savedState.gender
            const savedVariantId = savedState.variantId
            const savedSizeDisplayName = savedState.sizeDisplayName

            // Use the public restoreSelections method
            this.restoreSelections(savedGender, savedVariantId, savedSizeDisplayName)
        } catch (e) {
            // Silently fail if there's an error parsing saved state
            console.error('Error restoring sticky ATC selections:', e)
        }
    }
}